import { FC, useEffect, useMemo } from 'react';

import classNames from 'classnames';
import { DOT, NON_BREAKING_SPACE, SPACE } from 'utils/constants/common';
import { MagritteNotificationName } from 'utils/constants/notification';
import { conversion } from 'utils/conversion';

import { multicreationAlertFailedElementShownRaw } from '@hh.ru/analytics-js-events/build/clickme/employer/auction/multicreation/multicreation_alert_failed_element_shown';
import { multicreationAlertPartlyFailedElementShownRaw } from '@hh.ru/analytics-js-events/build/clickme/employer/auction/multicreation/multicreation_alert_partly_failed_element_shown';
import { multicreationAlertSuccessElementShownRaw } from '@hh.ru/analytics-js-events/build/clickme/employer/auction/multicreation/multicreation_alert_success_element_shown';
import multicreationBackButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/multicreation/multicreation_back_button_click';
import { ActionBar, Button, ProgressBar, Tag, Text, useBreakpoint } from '@hh.ru/magritte-ui';

import { formatToReactComponent } from 'bloko/common/trl';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';
import { useDashboardRoute } from 'hooks/useDashboardRoute';

import FormatMoney from 'components/FormatMoney';

import { fetchCreateAuctionBatch } from 'components/MultiCreationAuction/api/fetchCreateAuctionBatch';
import { sendMulticreationCampaignCreateFormSubmit } from 'components/MultiCreationAuction/api/sendMulticreationCampaignCreateFormSubmit';
import { CreationFailedAlert } from 'components/MultiCreationAuction/CreationFailedAlert/CreationFailedAlert';
import { CreationPartialFailedAlert } from 'components/MultiCreationAuction/CreationPartialFailedAlert/CreationPartialFailedAlert';
import { fetchVacancySearch } from 'components/MultiCreationAuction/VacanciesFiltersPanel/fetchVacancySearch';

import { BiddingStrategy } from 'store/models/auctionData';
import {
    MultiCreationAuctionSteps,
    selectMultiCreationCampaignsCreating,
    setMultiCreationActiveStep,
    setMultiCreationCampaignsCreating,
} from 'store/models/multiCreation';
import {
    addMagritteCustomNotification,
    addMagritteNotification,
    MagritteNotificationType,
} from 'store/models/notification';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/MultiCreationAuction/BidsAndBudgetsTable/BidsAndBudgetsTableBottomPanel/BidsAndBudgetsTableBottomPanel.module.less';

const TrlKeys = {
    back: 'MultiCreationAuction.back',
    createCampaigns: 'MultiCreationAuction.BidsAndBudgetsTable.createCampaigns',
    biddingStrategy: {
        label: 'MultiCreationAuction.BidsAndBudgetsTable.biddingStrategy.label',
        type: {
            [BiddingStrategy.Auto]: 'MultiCreationAuction.BidsAndBudgetsTable.biddingStrategy.type.auto',
            [BiddingStrategy.Manual]: 'MultiCreationAuction.BidsAndBudgetsTable.biddingStrategy.type.manual',
        },
    },
    budget: 'MultiCreationAuction.BidsAndBudgetsTable.budget',
    balance: 'MultiCreationAuction.BidsAndBudgetsTable.balance',
    vacancies: {
        zero: 'MultiCreationAuction.BidsAndBudgetsTable.vacancies.zero',
        one: 'MultiCreationAuction.BidsAndBudgetsTable.vacancies.one',
        some: 'MultiCreationAuction.BidsAndBudgetsTable.vacancies.some',
        many: 'MultiCreationAuction.BidsAndBudgetsTable.vacancies.many',
    },
    notification: {
        wrapper: 'MultiCreationAuction.notification.success',
        created: {
            default: 'MultiCreationAuction.notification.success.created.default',
            one: 'MultiCreationAuction.notification.success.created.one',
        },
        campaign: {
            zero: 'MultiCreationAuction.notification.success.campaign.zero',
            one: 'MultiCreationAuction.notification.success.campaign.one',
            some: 'MultiCreationAuction.notification.success.campaign.some',
            many: 'MultiCreationAuction.notification.success.campaign.many',
        },
    },
};

export const BidsAndBudgetsTableBottomPanel: FC = () => {
    const vacanciesAuctionInfo = useSelector(({ multiCreation }) => multiCreation.vacanciesAuctionInfo);
    const auctionsToCreate = useSelector(({ multiCreation }) => multiCreation.auctionsToCreate);
    const biddingStrategy = useSelector(({ multiCreation }) => multiCreation.biddingStrategy);
    const realBalance = useSelector(({ balance }) => balance?.realBalance);
    const isDynamicPriceEnabled = useSelector(({ user }) => user.user?.currentAccount.dynamicPriceEnabled);

    const isCampaignsCreating = useSelector(selectMultiCreationCampaignsCreating);
    const [isCreationFaildAlert, setIsCreationFaildAlertOn, setIsCreationFaildAlertOff] = useOnOffState(false);
    const [isCreationPartialFailedAlert, setIsCreationPartialFailedAlertOn, setIsCreationPartialFailedAlertOff] =
        useOnOffState(false);

    const { isMobile } = useBreakpoint();
    const trls = useTrls();
    const dispatch = useDispatch();
    const dashboardRoute = useDashboardRoute();

    useEffect(() => {
        return () => {
            if (isCampaignsCreating) {
                dispatch(setMultiCreationCampaignsCreating(false));
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const auctionsToCreateBudgetSum = useMemo(
        () => Object.values(auctionsToCreate).reduce((sum, { budget }) => sum + Number(budget), 0),
        [auctionsToCreate]
    );

    const vacanciesAuctionInfoCount = vacanciesAuctionInfo ? Object.keys(vacanciesAuctionInfo).length : 0;

    const progressBar = (
        <ProgressBar progress={isDynamicPriceEnabled ? 3 : 2} segments={isDynamicPriceEnabled ? 3 : 2} />
    );

    const handleCreateCampaigns = () => {
        const isValidationError = Object.values(auctionsToCreate).some(
            ({ bidValidationError, budgetValidationError, startAtValidationError, endAtValidationError }) =>
                bidValidationError || budgetValidationError || startAtValidationError || endAtValidationError
        );
        if (isValidationError) {
            dispatch(addMagritteNotification(MagritteNotificationName.MultiCreationAuctionValidationError));
            sendMulticreationCampaignCreateFormSubmit({
                auctionsToCreate,
                biddingStrategy,
                errors: 'validation errors',
            });
            return;
        }
        void dispatch(
            fetchCreateAuctionBatch({
                setIsLoading: (val) => dispatch(setMultiCreationCampaignsCreating(val)),
                callbackOnFail: (failedVacancyCount) => {
                    dispatch(setMultiCreationCampaignsCreating(false));
                    setIsCreationFaildAlertOn();
                    multicreationAlertFailedElementShownRaw({
                        failedVacancyCount,
                    });
                },
                callbackOnPartialFail: (createdCampaignCount, failedVacancyCount) => {
                    dispatch(setMultiCreationCampaignsCreating(false));
                    setIsCreationPartialFailedAlertOn();
                    multicreationAlertPartlyFailedElementShownRaw({
                        createdCampaignCount,
                        failedVacancyCount,
                    });
                },
                callbackOnSuccess: (createdCampaignCount) => {
                    dispatch(setMultiCreationCampaignsCreating(false));
                    const createdText = conversion(createdCampaignCount, {
                        zero: trls(TrlKeys.notification.created.default),
                        one: trls(TrlKeys.notification.created.one),
                        some: trls(TrlKeys.notification.created.default),
                        many: trls(TrlKeys.notification.created.default),
                    });
                    const campaignText = conversion(createdCampaignCount, {
                        zero: trls(TrlKeys.notification.campaign.zero),
                        one: trls(TrlKeys.notification.campaign.one),
                        some: trls(TrlKeys.notification.campaign.some),
                        many: trls(TrlKeys.notification.campaign.many),
                    });
                    void dispatch(
                        addMagritteCustomNotification({
                            type: MagritteNotificationType.Success,
                            text: `${createdText} ${createdCampaignCount}${NON_BREAKING_SPACE}${campaignText}`,
                        })
                    );
                    multicreationAlertSuccessElementShownRaw({
                        createdCampaignCount,
                    });
                },
                dashboardRoute,
            })
        );
    };

    const handleBackClick = () => {
        multicreationBackButtonClick({ currentStep: MultiCreationAuctionSteps.BidsAndBudgetsStep });

        if (isDynamicPriceEnabled) {
            dispatch(setMultiCreationActiveStep(MultiCreationAuctionSteps.BiddingStrategySelectStep));
        } else {
            void dispatch(fetchVacancySearch({}));
            dispatch(setMultiCreationActiveStep(MultiCreationAuctionSteps.VacanciesSelectStep));
        }
    };

    return (
        <>
            <ActionBar
                type={isMobile ? 'vertical' : 'horizontal'}
                isSticky
                showProgress={progressBar}
                primaryActions={
                    <Button
                        data-qa="create-button"
                        mode="primary"
                        style="accent"
                        onClick={handleCreateCampaigns}
                        loading={isCampaignsCreating}
                    >
                        {trls(TrlKeys.createCampaigns)}
                    </Button>
                }
                additionalContent={
                    <div className={styles.additionalContent}>
                        {isMobile && <div className={styles.progressBar}>{progressBar}</div>}
                        <div className={classNames(styles.additionalContent, styles.additionalContentText)}>
                            {isDynamicPriceEnabled && (
                                <>
                                    <Text data-qa="bidding-strategy" typography="label-1-regular">
                                        {trls(TrlKeys.biddingStrategy.label)}
                                        {SPACE}
                                        {trls(TrlKeys.biddingStrategy.type[biddingStrategy])}
                                    </Text>
                                    {!isMobile && <Text typography="label-1-regular">{DOT}</Text>}
                                </>
                            )}
                            <Text data-qa="budget-summary" typography="label-1-regular">
                                {formatToReactComponent(trls(TrlKeys.budget), {
                                    '[0]': (
                                        <>
                                            {NON_BREAKING_SPACE}
                                            {vacanciesAuctionInfoCount}
                                        </>
                                    ),
                                    '[1]': (
                                        <>
                                            {NON_BREAKING_SPACE}
                                            {conversion(vacanciesAuctionInfoCount, {
                                                zero: trls(TrlKeys.vacancies.zero),
                                                one: trls(TrlKeys.vacancies.one),
                                                some: trls(TrlKeys.vacancies.some),
                                                many: trls(TrlKeys.vacancies.many),
                                            })}
                                        </>
                                    ),
                                    '[2]': (
                                        <FormatMoney inCents groupSeparator={NON_BREAKING_SPACE}>
                                            {auctionsToCreateBudgetSum}
                                        </FormatMoney>
                                    ),
                                })}
                            </Text>
                        </div>
                        {auctionsToCreateBudgetSum / 100 > realBalance && (
                            <Tag style="negative" size={isMobile ? 'medium' : 'large'}>
                                <span data-qa="real-balance-tag">
                                    {formatToReactComponent(trls(TrlKeys.balance), {
                                        '[0]': (
                                            <FormatMoney groupSeparator={NON_BREAKING_SPACE}>{realBalance}</FormatMoney>
                                        ),
                                    })}
                                </span>
                            </Tag>
                        )}
                    </div>
                }
                secondaryActions={
                    <Button
                        data-qa="back-button"
                        mode="secondary"
                        style="accent"
                        onClick={handleBackClick}
                        disabled={isCampaignsCreating}
                    >
                        {trls(TrlKeys.back)}
                    </Button>
                }
            />
            <CreationFailedAlert
                isVisible={isCreationFaildAlert}
                closeAlert={setIsCreationFaildAlertOff}
                createCampaigns={handleCreateCampaigns}
            />
            <CreationPartialFailedAlert
                isVisible={isCreationPartialFailedAlert}
                closeAlert={setIsCreationPartialFailedAlertOff}
            />
        </>
    );
};
